/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { webpageReferenceApa7CopyString } from "@ndla/licenses";
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "@ndla/primitives";
import { styled } from "@ndla/styled-system/jsx";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { createPath } from "react-router";
import config from "../../config";
import type { GQLLicenseBox_ArticleFragment } from "../../graphqlTypes";
import { useRawLocation } from "../../util/localePath";
import { removeTrackingQueryParams } from "../../util/urlHelper";
import { AudioLicenseList } from "./AudioLicenseList";
import { ConceptLicenseList, GlossLicenseList } from "./ConceptLicenseList";
import { H5pLicenseList } from "./H5pLicenseList";
import { ImageLicenseList } from "./ImageLicenseList";
import { OembedItem } from "./OembedItem";
import { PodcastLicenseList } from "./PodcastLicenseList";
import { TextLicenseList, type TextItem } from "./TextLicenseList";
import { VideoLicenseList } from "./VideoLicenseList";

const StyledTabsRoot = styled(TabsRoot, {
  base: {
    paddingBlockStart: "xsmall",
    paddingBlockEnd: "xlarge",
    paddingInline: "xxsmall",
  },
});

function buildLicenseTabList(
  article: GQLLicenseBox_ArticleFragment,
  t: TFunction,
  copyText?: string | null,
  oembed?: string | null,
) {
  const metaData = article.transformedContent?.metaData;
  const tabs = [];
  const articleTexts: TextItem[] = [
    {
      title: article.title,
      copyright: article.copyright,
      updated: article.revised,
      copyText,
    },
  ];

  if (metaData?.textblocks.some((tb) => tb.copyright.license.license)) {
    metaData.textblocks.forEach((textblock) => {
      articleTexts.push({ title: textblock.title || "", copyright: textblock.copyright });
    });
  }

  tabs.push({
    title: t("license.tabs.text"),
    id: "text",
    content: <TextLicenseList texts={articleTexts} articleId={article.id} />,
  });

  if (metaData?.images.some((img) => img.copyright.license.license)) {
    tabs.push({
      title: t("license.tabs.images"),
      id: "images",
      content: <ImageLicenseList images={metaData.images} />,
    });
  }

  if (metaData?.brightcoves.some((brightcove) => brightcove.copyright?.license.license)) {
    tabs.push({
      title: t("license.tabs.video"),
      id: "video",
      content: <VideoLicenseList videos={metaData?.brightcoves} />,
    });
  }

  if (metaData?.audios.some((audio) => audio.copyright.license.license)) {
    tabs.push({
      title: t("license.tabs.audio"),
      id: "audio",
      content: <AudioLicenseList audios={metaData.audios} />,
    });
  }

  if (metaData?.podcasts.some((podcast) => podcast.copyright.license.license)) {
    tabs.push({
      title: t("license.tabs.podcast"),
      id: "podcast",
      content: <PodcastLicenseList podcasts={metaData.podcasts} />,
    });
  }

  if (metaData?.h5ps.some((h5p) => h5p.copyright?.license.license)) {
    tabs.push({
      title: t("license.tabs.h5p"),
      id: "h5p",
      content: <H5pLicenseList h5ps={metaData.h5ps} />,
    });
  }

  if (metaData?.concepts.some((concept) => concept.copyright?.license?.license)) {
    tabs.push({
      title: t("license.tabs.concept"),
      id: "concept",
      content: <ConceptLicenseList concepts={metaData.concepts} />,
    });
  }

  if (metaData?.glosses.some((gloss) => gloss.copyright?.license?.license)) {
    tabs.push({
      title: t("license.tabs.gloss"),
      id: "gloss",
      content: <GlossLicenseList glosses={metaData.glosses} />,
    });
  }

  if (oembed) {
    tabs.push({
      title: t("license.tabs.embedlink"),
      id: "embedLink",
      content: <OembedItem oembed={oembed} />,
    });
  }

  return tabs;
}

export const useArticleCopyText = (article: GQLLicenseBox_ArticleFragment | undefined) => {
  const { t, i18n } = useTranslation();
  const location = useRawLocation();
  const cleanedHref = removeTrackingQueryParams(createPath(location));
  if (!article) return undefined;
  const [day, month, year] = article.revised.split(".").map((s) => parseInt(s));
  const published = new Date(year!, month! - 1, day!).toUTCString();
  return webpageReferenceApa7CopyString(
    article.title,
    undefined,
    published,
    `${config.ndlaFrontendDomain}${cleanedHref}`,
    article.copyright,
    i18n.language,
    "",
    t,
  );
};

interface Props {
  article: GQLLicenseBox_ArticleFragment;
  copyText?: string | null;
  oembed: string | null;
}
export const LicenseBox = ({ article, copyText, oembed }: Props) => {
  const { t } = useTranslation();
  const tabs = buildLicenseTabList(article, t, copyText, oembed);
  return (
    <StyledTabsRoot
      defaultValue={tabs[0]?.id}
      orientation="horizontal"
      variant="line"
      translations={{ listLabel: t("tabs.licenseBox") }}
    >
      <TabsList>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.id}>
            {tab.title}
          </TabsTrigger>
        ))}
        <TabsIndicator />
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id}>
          {tab.content}
        </TabsContent>
      ))}
    </StyledTabsRoot>
  );
};

LicenseBox.fragments = {
  article: gql`
    fragment LicenseBox_Article on Article {
      id
      title
      htmlTitle
      published
      revised
      copyright {
        ...TextLicenseList_Copyright
      }

      transformedContent(transformArgs: $transformArgs) {
        metaData {
          copyText
          concepts {
            ...ConceptLicenseList_ConceptLicense
          }
          glosses {
            ...GlossLicenseList_GlossLicense
          }
          h5ps {
            ...H5pLicenseList_H5pLicense
          }
          brightcoves {
            ...VideoLicenseList_BrightcoveLicense
          }
          audios {
            ...AudioLicenseList_AudioLicense
          }
          podcasts {
            ...PodcastLicenseList_PodcastLicense
          }
          images {
            ...ImageLicenseList_ImageLicense
          }
          textblocks {
            title
            copyright {
              ...TextLicenseList_Copyright
            }
          }
        }
      }
    }
    ${GlossLicenseList.fragments.gloss}
    ${ConceptLicenseList.fragments.concept}
    ${H5pLicenseList.fragments.h5p}
    ${VideoLicenseList.fragments.video}
    ${AudioLicenseList.fragments.audio}
    ${PodcastLicenseList.fragments.podcast}
    ${ImageLicenseList.fragments.image}
    ${TextLicenseList.fragments.copyright}
  `,
};
